import { routes } from '@/app/app';
import { AutocompleteType, SnackbarType } from '@/context/context';

export const sortArr = (arr: any[], propertyName: string) => {
  return arr.sort((a, b) => {
    const valA = a[propertyName];
    const valB = b[propertyName];
    if (valA < valB) {
      return -1;
    }
    if (valA > valB) {
      return 1;
    }

    // names must be equal
    return 0;
  });
};

const getPathPrefix = (routePath: string) => {
  const segments = routePath.split('/').filter(Boolean);
  const paramIdx = segments.findIndex((s) => s.startsWith(':'));
  const prefixSegments = paramIdx === -1 ? segments : segments.slice(0, paramIdx);
  return prefixSegments.length > 0 ? '/' + prefixSegments.join('/') : '/';
};

export const isPublicPath = (path: string) => {
  const normalized = path?.replace(/\/$/, '') || '/';
  return routes.some((route) => {
    if (!route.isPublic) return false;
    const prefix = getPathPrefix(route.route);
    return normalized === prefix || normalized.startsWith(prefix + '/');
  });
};

export const getShaliachById = (id: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.value.split('-')[1] === id);
};

export const getBranchById = (id: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.value.split('-')[0] === id);
};

export const getShaliachIdFromValue = (value: string) => {
  return value.split('-')[1];
};

export const getBranchIdFromValue = (value: string) => {
  return value.split('-')[0];
};

export const getShaliachNameById = (shaliachId: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.value.split('-')[1] === shaliachId)?.label || '';
};

export const getShaliachIdByName = (shaliachName: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.label === shaliachName)?.value.split('-')[1] || '';
};

export const getBranchNameById = (branchId: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.value.split('-')[0] === branchId)?.label || '';
};

export const getBranchIdByName = (branchName: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.find(option => option.label === branchName)?.value.split('-')[0] || '';
};

export const getShluchimOfBranch = (branchId: string, allShluchim: AutocompleteType[]) => {
  return allShluchim
    .filter(option => option.value.split('-')[0] === branchId)
    .map(option => ({
      label: option.label,
      value: option.value.split('-')[1]
    }));
};

export const getShluchimOfBranchNames = (branchId: string, allShluchim: AutocompleteType[]) => {
  return allShluchim.filter(option => option.value.split('-')[0] === branchId).map(option => option.label);
};

export const getShluchimOfBranchIds = (branchId: string, allShluchim: AutocompleteType[]) => {
  return allShluchim
    .filter(option => option.value.split('-')[0] === branchId)
    .map(option => option.value.split('-')[1]);
};

export const getShluchimNames = (shluchimIds: string[], allShluchim: AutocompleteType[]) => {
  return shluchimIds
    .map(id => allShluchim.find(option => option.value.split('-')[1] === id)?.label || '')
    .filter(Boolean);
};

export const getShluchimIds = (shluchimNames: string[], allShluchim: AutocompleteType[]) => {
  return shluchimNames
    .map(name => allShluchim.find(option => option.label === name)?.value.split('-')[1] || '')
    .filter(Boolean);
};

/**
 * Generate a Waze navigation link
 */
export const generateWazeAddressLink = (address: string | undefined): string => {
  if (!address) {
    return '';
  }
  return `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
};

/**
 * Legacy copy via a hidden textarea — for WebViews / insecure contexts where
 * navigator.clipboard is missing or rejects.
 */
const copyWithExecCommand = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  // iOS zooms into inputs with font-size < 16px
  textarea.style.fontSize = '16px';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

const writeToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    if (!copyWithExecCommand(text)) {
      throw error;
    }
  }
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (
  text: string,
  setSnackbar?: (snackbar: SnackbarType) => void,
  successMessage = 'הלינק הועתק ללוח'
): Promise<boolean> => {
  try {
    await writeToClipboard(text);
    setSnackbar?.({
      open: true,
      message: successMessage,
      severity: 'success'
    });
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    setSnackbar?.({
      open: true,
      message: 'שגיאה בשיתוף',
      severity: 'error'
    });
    return false;
  }
};

/**
 * Generic share utility using Web Share API or fallback to clipboard
 */
export const shareContent = async (
  title: string,
  text: string,
  url: string,
  setSnackbar?: (snackbar: SnackbarType) => void
): Promise<boolean> => {
  const shareData = {
    title,
    text,
    url
  };

  if (navigator.share) {
    try {
      await navigator.share(shareData);
      setSnackbar?.({
        open: true,
        message: 'שותף בהצלחה!',
        severity: 'success'
      });
      return true;
    } catch (error) {
      console.error('Error sharing:', error);
      const copied = await copyToClipboard(`${title}\n${text}\n${url}`, setSnackbar);
      if (copied) {
        return true;
      }
      setSnackbar?.({
        open: true,
        message: 'שגיאה בשיתוף',
        severity: 'error'
      });
      return false;
    }
  } else {
    // Fallback to copying to clipboard
    const copied = await copyToClipboard(`${title}\n${text}\n${url}`, setSnackbar);
    return copied;
  }
};

/**
 * Generic calendar link generator for Google Calendar
 */
export const generateCalendarLink = (
  title: string,
  startDate: Date | string,
  endDate?: Date | string,
  description?: string,
  location?: string
): string => {
  const formatDate = (date: Date): string => {
    // Validate that the date is valid before formatting
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date provided to formatDate');
    }
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const parseDate = (date: Date | string): Date => {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      // Fallback to current date if invalid
      return new Date();
    }
    return dateObj;
  };

  const start = formatDate(parseDate(startDate));
  const end = endDate ? formatDate(parseDate(endDate)) : start;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${start}/${end}`,
    details: description || '',
    location: location || '',
    sf: 'true',
    output: 'xml'
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
